'use client';

import React, { useState, useEffect } from 'react';
import MetricCard from '@/components/ui/MetricCard';
import { ClipboardList, AlertTriangle, Car, Wrench, PenLine, Package } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import {
  getActiveJobCardCount,
  getAwaitingSignatureCount,
  getVehiclesServicedThisWeek,
  getTotalPartsConsumed,
} from '@/lib/jobCardStore';
import { getPendingCosmeticCount } from '@/lib/cosmeticStore';

interface KpiData {
  activeJobs: number;
  lowStock: number;
  vehiclesThisWeek: number;
  cosmeticPending: number;
  partsConsumed: number;
  awaitingSignature: number;
}

async function fetchKpis(): Promise<KpiData> {
  const supabase = createClient();
  const { data: parts } = await supabase
    .from('parts')
    .select('stock, min_stock');
  const lowStock = (parts || []).filter(
    (p) => (p.stock as number) > 0 && (p.stock as number) < (p.min_stock as number)
  ).length;

  const [activeJobs, awaitingSignature, vehiclesThisWeek, partsConsumed, cosmeticPending] =
    await Promise.all([
      getActiveJobCardCount(),
      getAwaitingSignatureCount(),
      getVehiclesServicedThisWeek(),
      getTotalPartsConsumed(),
      getPendingCosmeticCount(),
    ]);

  return { activeJobs, lowStock, vehiclesThisWeek, cosmeticPending, partsConsumed, awaitingSignature };
}

export default function KpiBentoGrid() {
  const [kpi, setKpi] = useState<KpiData>({
    activeJobs: 0,
    lowStock: 0,
    vehiclesThisWeek: 0,
    cosmeticPending: 0,
    partsConsumed: 0,
    awaitingSignature: 0,
  });

  const refresh = () => {
    fetchKpis().then(setKpi).catch(console.error);
  };

  useEffect(() => {
    refresh();

    // Real-time subscriptions for live KPI updates
    const supabase = createClient();

    const jobCardsChannel = supabase
      .channel('kpi_job_cards')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_cards' }, () => {
        refresh();
      })
      .subscribe();

    const partsChannel = supabase
      .channel('kpi_parts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parts' }, () => {
        refresh();
      })
      .subscribe();

    const cosmeticChannel = supabase
      .channel('kpi_cosmetic')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'cosmetic_items' }, () => {
        refresh();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(jobCardsChannel);
      supabase.removeChannel(partsChannel);
      supabase.removeChannel(cosmeticChannel);
    };
  }, []);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 2xl:grid-cols-6 gap-4">
      {/* Hero metric — Active Jobs — spans 2 cols */}
      <div className="col-span-2 md:col-span-2 xl:col-span-2">
        <MetricCard
          label="Active Job Cards"
          value={String(kpi.activeJobs)}
          subtext={`${kpi.awaitingSignature} awaiting sign-off`}
          icon={ClipboardList}
          trend="up"
          trendValue="Live count"
          variant="default"
          className="h-full"
        />
      </div>

      {/* Parts below reorder — warning state */}
      <div className="col-span-1 xl:col-span-1">
        <MetricCard
          label="Low Stock Alerts"
          value={String(kpi.lowStock)}
          subtext="Parts below reorder level"
          icon={AlertTriangle}
          trend="down"
          trendValue="Live count"
          variant="warning"
          className="h-full"
        />
      </div>

      {/* Vehicles serviced this week */}
      <div className="col-span-1 xl:col-span-1">
        <MetricCard
          label="Vehicles This Week"
          value={String(kpi.vehiclesThisWeek)}
          subtext="Unique vehicles serviced"
          icon={Car}
          trend="up"
          trendValue="This week"
          variant="positive"
          className="h-full"
        />
      </div>

      {/* Open cosmetic items — danger */}
      <div className="col-span-1 xl:col-span-1">
        <MetricCard
          label="Cosmetic Pending"
          value={String(kpi.cosmeticPending)}
          subtext="Items not yet completed"
          icon={PenLine}
          trend="neutral"
          trendValue="Live count"
          variant="danger"
          className="h-full"
        />
      </div>

      {/* Parts consumed — all time total qty */}
      <div className="col-span-1 xl:col-span-1">
        <MetricCard
          label="Parts Consumed"
          value={String(kpi.partsConsumed)}
          subtext="Total qty across all jobs"
          icon={Package}
          trend="up"
          trendValue="All time"
          variant="warning"
          className="h-full"
        />
      </div>

      {/* Jobs awaiting signature */}
      <div className="col-span-2 md:col-span-1 xl:col-span-1">
        <MetricCard
          label="Awaiting Signature"
          value={String(kpi.awaitingSignature)}
          subtext="Branch rep sign-off required"
          icon={Wrench}
          trend="down"
          trendValue="Live count"
          variant="warning"
          className="h-full"
        />
      </div>
    </div>
  );
}